/**
 * Whether the user's device supports fine-grained pointer
 */
export const supportsHover = matchMedia('(hover: hover) and (pointer: fine)').matches

/**
 * Check whether the current browser supports a given selector
 *
 * @param {string} selector
 * @returns {boolean}
 */
export function supportsSelector(selector) {
  let style = document.createElement('style')
  document.head.appendChild(style)
  let sheet = style.sheet

  try {
    sheet.insertRule(`${selector} {}`)
    return true
  } catch {
    return false
  } finally {
    style.remove()
  }
}

/**
 * Replace HTML inside a string with entities
 *
 * @param {string} string
 * @returns {string}
 */
export function escapeHTML(string) {
  return String(string)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/**
 * Get a v4 UUID
 *
 * @returns {string}
 */
export function uuid() {
  if ('randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID()
  }

  let s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1)

  return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
}

/**
 * Check whether a value is a plain object
 *
 * @param {any} value
 * @returns {boolean}
 */
export function isPlainObject(value) {
  if (typeof value !== 'object') return false
  if (value === null) return false

  let prototype = Object.getPrototypeOf(value)
  if (prototype === null) return true
  if (prototype === Object.prototype) return true

  return false
}

/**
 * Check whether two values are equal
 *
 * @param {any} a
 * @param {any} b
 * @returns {boolean}
 */
export function equals(a, b) {
  let isArrayA = Array.isArray(a)
  let isArrayB = Array.isArray(b)

  if (isArrayA !== isArrayB) return false

  // Compare arrays by item equality
  if (isArrayA && isArrayB) {
    if (a.length !== b.length) return false

    for (let i = 0; i < a.length; i++) {
      if (!equals(a[i], b[i])) return false
    }

    return true
  }

  let isPlainObjectA = isPlainObject(a)
  let isPlainObjectB = isPlainObject(b)

  if (isPlainObjectA !== isPlainObjectB) return false

  // Compare plain objects by key-value equality
  if (isPlainObjectA && isPlainObjectB) {
    let keysA = Object.keys(a)
    let keysB = Object.keys(b)

    if (keysA.length !== keysB.length) return false

    for (let key of keysA) {
      if (!equals(a[key], b[key])) return false
    }

    return true
  }

  // Fallback: Compare by identity
  return a === b
}

/**
 * Create a Node from an HTML template string, escaping all interpolated values, with special handling for:
 * - strings (escaped)
 * - DOM Nodes (inserted as-is, retaining things like event handlers)
 * - arrays (handles items as if they were each inserted on their own)
 *
 * There used to be support for interpolating functions as event handlers, but since this bloated
 * the code significantly and had not been needed thanks to Alpine.js, it was removed.
 *
 * The original code can still be found here:
 * https://gist.github.com/loilo/c3f1cb8bc2edb84be00b2795787ba8ff
 *
 * @param {string|string[]} strings
 * @param {...unknown} values
 * @returns {Node}
 *
 * @example
 * ```
 * let button = fragment`<button>Hit me!</button>`
 * ```
 */
export function fragment(strings, ...values) {
  if (typeof strings === 'string') {
    strings = [strings]
  }

  let replacedNodes = new Map()
  let getReplacementTagHtml = id => `<template id="${id}"></template>`

  let getEscapedHtml = value => {
    let escapedString

    if (Array.isArray(value)) {
      return value.map(getEscapedHtml).join('')
    } else if (value instanceof Node) {
      let id = `replacement-${uuid()}`
      replacedNodes.set(id, value)
      escapedString = getReplacementTagHtml(id)
    } else {
      escapedString = escapeHTML(value)
    }

    return escapedString
  }

  let template = document.createElement('template')
  let templateHtml = strings
    .reduce((carry, current, index) => carry + getEscapedHtml(values[index - 1]) + current)
    .trim()

  template.innerHTML = templateHtml

  // Replace placeholders
  for (let [id, element] of replacedNodes) {
    let placeholder = template.content.getElementById(id)

    if (placeholder) {
      placeholder.insertAdjacentElement('afterend', element)
      placeholder.remove()
    }
  }

  if (template.content.childNodes.length === 1) {
    return template.content.firstChild
  } else {
    return template.content
  }
}

let focusableSelectors = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
]

let focusableSelector = focusableSelectors.join(', ')

/**
 * Check if the given element is focusable
 *
 * @param {Element} element
 */
export function isFocusable(element) {
  if (!element.matches(focusableSelector)) return false
  if (element.tabIndex === -1) return false

  return true
}

/**
 * Get the next element in the DOM tree
 *
 * @param {Element} reference
 * @param {((element: Element) => boolean)|undefined} filter
 * @returns {Element|undefined}
 */
export function getNextElement(reference, filter) {
  let iterator = document.createNodeIterator(document.body, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      // Reject the reference itself
      if (node === reference) return NodeFilter.FILTER_REJECT

      let position = node.compareDocumentPosition(reference)

      // Skip ancestors
      if (position & Node.DOCUMENT_POSITION_CONTAINED_BY) return NodeFilter.FILTER_SKIP

      // Skip predecessors
      if (position & Node.DOCUMENT_POSITION_FOLLOWING || position & Node.DOCUMENT_POSITION_CONTAINS)
        return NodeFilter.FILTER_REJECT

      // Skip invalid elements
      if (typeof filter === 'function' && !filter(node)) return NodeFilter.FILTER_SKIP

      return NodeFilter.FILTER_ACCEPT
    },
  })

  return iterator.nextNode() || undefined
}

/**
 * Create a debounced version of a function
 *
 * @param {number} wait
 * @param {function} fn
 * @returns {function}
 */
export function debounce(wait, fn) {
  let timeout
  return (...args) => {
    let later = () => {
      timeout = null
      fn(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export function throttle(wait, fn, { leading = true, trailing = true } = {}) {
  let timeout
  let lastCallTime = 0

  return (...args) => {
    let now = Date.now()

    if (!lastCallTime && !leading) {
      lastCallTime = now
    }

    let remaining = wait - (now - lastCallTime)

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      lastCallTime = now
      fn.call(this, ...args)
    } else if (!timeout && trailing) {
      timeout = setTimeout(() => {
        timeout = null
        lastCallTime = leading ? Date.now() : 0
        fn.call(this, ...args)
      }, remaining)
    }
  }
}

/**
 * @template T
 * @param {T[]} array
 * @returns {T[]}
 */
export function shuffle(array) {
  const arrayCopy = [...array]
  let currentIndex = arrayCopy.length

  while (currentIndex != 0) {
    const randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex--
    ;[arrayCopy[currentIndex], arrayCopy[randomIndex]] = [
      arrayCopy[randomIndex],
      arrayCopy[currentIndex],
    ]
  }

  return arrayCopy
}
